import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { AsyncUserMultiSelect } from "components/Common/AsyncSearchSelects";
import {
  addTaskBucket,
  getTaskBucketGroupList,
  submitTaskBucketFilterData,
  updateTaskBucket,
} from "model/TaskBucketModel";
import { toast } from "react-toastify";

const TASK_BUCKET_SYSTEM_USERS_PAGE_NAME = "TaskBucket";

/** Map `users: [{ id, name }, …]` from list/detail payloads to react-select options. */
const mapUsersToSelectOptions = (users) => {
  if (!Array.isArray(users)) return [];
  return users
    .map((u) => {
      const id = String(u?.id ?? u?._id ?? u?.userId ?? u?.user_id ?? "").trim();
      if (!id) return null;
      const label =
        String(u?.name ?? u?.userName ?? u?.fullName ?? u?.Name ?? "").trim() || id;
      return { value: id, label };
    })
    .filter(Boolean);
};

/** Resolve selected user IDs from POST/GET payload (`users`, `userIds`, or CSV fields). */
const parseUserIdsFromRow = (row) => {
  if (!row || typeof row !== "object") return [];
  const fromArray = Array.isArray(row?.userIds)
    ? row.userIds
    : Array.isArray(row?.defaultUserIds)
      ? row.defaultUserIds
      : Array.isArray(row?.users)
        ? row.users
        : [];
  if (fromArray.length) {
    return [
      ...new Set(
        fromArray
          .map((x) => {
            if (x != null && typeof x === "object") {
              return String(x?.id ?? x?._id ?? x?.userId ?? x?.user_id ?? "").trim();
            }
            return String(x ?? "").trim();
          })
          .filter(Boolean)
      ),
    ];
  }
  const csv = String(row?.defaultuserId ?? row?.defaultUserId ?? row?.defaultUser ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return [...new Set(csv)];
};

const resolveSelectedUsersFromRow = (row) => {
  const fromUsers = mapUsersToSelectOptions(row?.users);
  if (fromUsers.length) return fromUsers;
  return parseUserIdsFromRow(row).map((id) => ({ value: id, label: id }));
};

/** Resolve rows from `master/TaskBucket/filter` `{ data: [...] }` payload. */
const resolveTaskBucketFilterRows = (payload) => {
  const raw = payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : raw?.records ?? raw?.rows ?? [];
  return Array.isArray(list) ? list : [];
};

const findTaskBucketRowById = (rows, id) => {
  const targetId = String(id ?? "").trim();
  if (!targetId) return undefined;
  return (Array.isArray(rows) ? rows : []).find(
    (row) => String(row?.id ?? row?._id ?? "") === targetId
  );
};

const buildFormDataFromRow = (row) => {
  const groupIdRaw =
    row?.groupId ??
    row?.group?.id ??
    row?.taskBucketGroupId ??
    row?.TaskBucketGroupId ??
    "";

  return {
    title: row?.title || "",
    selectedUsers: resolveSelectedUsersFromRow(row),
    groupId: groupIdRaw ? String(groupIdRaw) : "",
    status: row?.status === true || row?.status === "active",
  };
};

const initialFormData = {
  title: "",
  selectedUsers: [],
  groupId: "",
  status: true,
};

const TaskBucketAddEditModal = ({
  setTaskBucketAddEditModalOpen,
  taskBucketAddEditModalOpen,
  getTaskBucketList,
  itemId,
  editRow = null,
}) => {
  const [formData, setFormData] = useState({ ...initialFormData });
  const [groupData, setGroupData] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const resetForm = () => {
    setFormData({ ...initialFormData });
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitTaskBucket();
  };

  const closeModal = () => {
    resetForm();
    setTaskBucketAddEditModalOpen(false);
  };

  useEffect(() => {
    getGroupList();
  }, []);

  const getGroupList = async () => {
    setGroupLoading(true);
    const response = await getTaskBucketGroupList({});
    const groups = response?.data ?? [];
    setGroupData(Array.isArray(groups) ? groups : []);
    setGroupLoading(false);
  };

  const groupOptions = groupData?.map((item) => ({
    value: item?.id,
    label: item?.title || "",
  }));

  const submitTaskBucket = async () => {
    const nextErrors = {};
    if (!String(formData?.title ?? "").trim()) {
      nextErrors.title = "Title is required.";
    }
    if (!String(formData?.groupId ?? "").trim()) {
      nextErrors.groupId = "Select Group is required.";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast.error(Object.values(nextErrors)[0]);
      return;
    }
    setErrors({});

    const userIds = (formData?.selectedUsers || [])
      .map((opt) => String(opt?.value ?? "").trim())
      .filter(Boolean);

    const payload = {
      title: String(formData?.title ?? "").trim(),
      userIds,
      groupId: String(formData?.groupId ?? "").trim(),
      status: formData?.status ? "active" : "deactive",
    };

    const response = itemId
      ? await updateTaskBucket(itemId, payload)
      : await addTaskBucket(payload);

    if (response?.success) {
      toast.success(response?.message || "Task Bucket saved successfully");
      getTaskBucketList?.();
      if (!itemId) {
        resetForm();
      }
      closeModal();
    } else {
      toast.error(response?.message || "Unable to save Task Bucket");
    }
  };

  useEffect(() => {
    if (!taskBucketAddEditModalOpen) return;

    const loadTaskBucketForEdit = async () => {
      if (!itemId) {
        resetForm();
        return;
      }

      const editRowId = String(editRow?.id ?? editRow?._id ?? "").trim();
      if (editRow && editRowId === String(itemId)) {
        setFormData(buildFormDataFromRow(editRow));
        setErrors({});
        return;
      }

      const response = await submitTaskBucketFilterData({});
      const row = findTaskBucketRowById(resolveTaskBucketFilterRows(response), itemId);
      if (!row) {
        resetForm();
        return;
      }

      setFormData(buildFormDataFromRow(row));
      setErrors({});
    };

    loadTaskBucketForEdit();
  }, [taskBucketAddEditModalOpen, itemId, editRow]);

  return (
    <Modal
      isOpen={taskBucketAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">Task Bucket</h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          {/* Title */}
          <FormGroup>
            <Label>Title <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="title"
              placeholder="Title"
              value={formData.title}
              invalid={!!errors.title}
              onChange={handleChange}
            />
            <FormFeedback>{errors.title}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label>Default User</Label>
            <AsyncUserMultiSelect
              key={`task-bucket-users-${itemId || "new"}`}
              pageName={TASK_BUCKET_SYSTEM_USERS_PAGE_NAME}
              value={formData.selectedUsers}
              onChange={(selectedOptions) =>
                setFormData((prev) => ({
                  ...prev,
                  selectedUsers: Array.isArray(selectedOptions) ? selectedOptions : [],
                }))
              }
              placeholder="Select Default User"
            />
          </FormGroup>

          <FormGroup>
            <Label>Select Group <span className="text-danger">*</span></Label>
            <Select
              isLoading={groupLoading}
              options={groupOptions}
              className={`select2-selection${errors.groupId ? " is-invalid" : ""}`}
              placeholder="Select Group"
              value={
                groupOptions.find(
                  (option) => String(option.value) === String(formData.groupId || "")
                ) || null
              }
              onChange={(selectedOption) => {
                setFormData((prev) => ({
                  ...prev,
                  groupId: selectedOption?.value ? String(selectedOption.value) : "",
                }));
                if (errors.groupId) {
                  setErrors((prev) => ({ ...prev, groupId: "" }));
                }
              }}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
            {errors.groupId && (
              <FormFeedback className="d-block">{errors.groupId}</FormFeedback>
            )}
          </FormGroup>

          {/* Status Field */}
          <FormGroup>
            <Label>Status</Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData.status ? "Active" : "Inactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    key={`task-bucket-status-${itemId || "new"}-${formData?.status}`}
                    defaultChecked={formData?.status}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
              Save
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default TaskBucketAddEditModal;
